import { useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { IonSpinner } from '@ionic/react';
import { setContext } from '@sentry/react';

import styles from './SignIn.module.scss';

import {
  defaultError,
  invalidParametersError
} from '../../config/errors.config';
import { APP_TITLE } from '../../constants/app';
import { SENTRY_SERVICE_CONTEXT } from '../../constants/sentry';
import {
  sessionAuthTokenKey,
  sessionHostKey,
  sessionIdeKey,
  sessionMachineKey,
  sessionOrgKey,
  sessionRepoKey,
  sessionRequestCodeVerifierKey,
  sessionRequestStateKey,
  sessionSchemeKey,
  sessionServiceKey,
  sessionUserTokenKey,
  sessionUsernameKey,
  sessionValidationKey
} from '../../constants/storage';
import { getService } from '../../services';
import { ServiceError } from '../../services/errors';
import { Service } from '../../services/service';
import { ErrorCard } from '../../stories/ErrorCard/ErrorCard';
import { SignInCard } from '../../stories/SigninCard/SignInCard';
import { generateCodeChallenge } from '../../util/codeChallenge';
import { getUrlParams } from '../../util/params';
import { generateRequestState } from '../../util/randomState';
import { remove, set } from '../../util/sessionStorage';

interface SignInParams {
  host: string;
  ide: string;
  machine: string;
  org: string;
  repo?: string;
  scheme: string;
  service: string;
  use_token: string;
}

export default function SignIn() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [service, setService] = useState<Service>();
  const [oauthUrl, setOauthUrl] = useState<string>();
  const [tokenUrl, setTokenUrl] = useState<string>();
  const [useToken, setUseToken] = useState<boolean>(false);
  const [error, setError] = useState<ServiceError>();

  useEffect(() => {
    setup();
  }, [searchParams]);

  const setup = async () => {
    try {
      const params = getUrlParams<SignInParams>(searchParams);

      setContext(SENTRY_SERVICE_CONTEXT, {
        host: params.host,
        org: params.org,
        repo: !!params.repo,
        service: params.service,
        ide: params.ide,
        machine: !!params.machine,
        scheme: params.scheme
      });

      if (
        !params.service ||
        !params.host ||
        !params.org ||
        !params.scheme ||
        !params.ide
      ) {
        throw new ServiceError(
          invalidParametersError.title,
          invalidParametersError.message
        );
      }

      const codeService = getService(params.service, params.host, true, null);

      // Clear some of the stored values if they exist
      remove(sessionAuthTokenKey);
      remove(sessionValidationKey);
      remove(sessionUserTokenKey);
      remove(sessionUsernameKey);
      remove(sessionRepoKey);
      remove(sessionMachineKey);

      // Store the parameters
      set(sessionServiceKey, params.service);
      set(sessionHostKey, params.host);
      set(sessionOrgKey, params.org);
      set(sessionSchemeKey, params.scheme);
      set(sessionIdeKey, params.ide);
      if (params.machine) set(sessionMachineKey, params.machine);
      if (params.repo) set(sessionRepoKey, params.repo);

      // Generate OAuth URL
      const state = generateRequestState();
      set(sessionRequestStateKey, state);

      const { codeVerifier, codeChallenge } = await generateCodeChallenge();
      set(sessionRequestCodeVerifierKey, codeVerifier);

      setOauthUrl(codeService.getOAuthLink(state, codeChallenge));

      // Generate token URL
      setTokenUrl(
        codeService.getTokenUrl(params.org, params.ide, params.machine)
      );

      setService(codeService);
      setUseToken(params.use_token === 'true');
    } catch (err: any) {
      if (err instanceof ServiceError) {
        setError(err);
      } else {
        setError(
          new ServiceError(
            defaultError.title,
            err.message || defaultError.message
          )
        );
      }
    }
  };

  const handleTokenSignInClicked = (token: string, username?: string) => {
    set(sessionUserTokenKey, token);
    username && set(sessionUsernameKey, username);
    navigate('/oauth/complete?token=true');
  };

  return (
    <section className={styles.container}>
      <Helmet>
        <title>{APP_TITLE} - Sign In</title>
      </Helmet>
      {error ? (
        <ErrorCard title={error.name} message={error.message} />
      ) : (
        <>
          {service && (
            <SignInCard
              service={service}
              oauthUrl={oauthUrl!}
              tokenUrl={tokenUrl!}
              onTokenSignInClicked={handleTokenSignInClicked}
              useToken={useToken}
            />
          )}
          {!service && (
            <IonSpinner
              color="light"
              name="circles"
              style={{ color: 'white' }}
            />
          )}
        </>
      )}
    </section>
  );
}
